import { MarketplaceSubGroup } from "@shared/types";
import { TableLoadingEmpty } from "common/styles";
import Table from "common/Table/Table";
import React, { ReactNode, useContext } from "react";
import { ColumnsType } from "antd/es/table";
import {
  sortByNumberProperty,
  sortByStringProperty,
} from "@shared/utils/general";
import ScoreTag from "features/newMarketplace/common/ScoreTag";
import styled, { useTheme } from "styled-components";
import Title from "common/Typography/Title";
import GroupTags from "features/newMarketplace/common/GroupTags";
import GroupDollarRate from "features/newMarketplace/common/GroupDollarRate";
import FreeOrFreeTrialTag from "features/newMarketplace/common/FreeOrFreeTrialTag";
import GroupRate from "features/newMarketplace/common/GroupRate";
import ChangeTag from "common/Tags/ChangeTag";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import {
  getNetPnlChange,
  getPnlSortCallback,
  getRiskSortCallback,
} from "@shared/features/newMarketplace/utils";
import PnlChart from "features/newMarketplace/common/PnlChart";
import RiskChart from "features/newMarketplace/common/RiskChart";
import { buildMarketPlaceGroupEvent } from "utils/analytics";
import { getGroupExtraDetailsRoute } from "utils/routes";
import { AnalyticsContext } from "analytics/AnalyticsProvider";
import { useHistory } from "react-router-dom";
import { GroupsSort } from "@shared/features/newMarketplace/types";
import { SortingOrder } from "types";
import { SORT_ORDER_MAPPING } from "common/constants";
import { getTableOnChangeFunction } from "common/Table/utils";
import { OPPOSITE_SORTS } from "@shared/features/newMarketplace/constants";
import Tooltip from "common/Tooltips/Tooltip";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { FIELD_NAME_TO_SORT } from "features/newMarketplace/common/constants";
import NewLookGroupsTable from "features/newMarketplace/NewLook/NewLookGroupsTable";

export interface GroupsTableProps {
  groups: MarketplaceSubGroup[];
  loading?: boolean;
  openScoreModal?: (group: MarketplaceSubGroup) => void;
  sortBy: GroupsSort;
  setSortBy: (sortBy: GroupsSort) => void;
  sortDirection: SortingOrder;
  setSortDirection: (sortDirection: SortingOrder) => void;
  emptyState?: ReactNode;
}

const ClassicGroupsTable = ({
  groups,
  loading,
  openScoreModal,
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  emptyState,
}: GroupsTableProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { analytics } = useContext(AnalyticsContext);
  const history = useHistory();

  const columns: ColumnsType<MarketplaceSubGroup> = [
    {
      title: t("table.columns.score"),
      dataIndex: "backtesting_score",
      defaultSortOrder: "descend",
      width: 100,
      sortOrder:
        sortBy === GroupsSort.SCORE
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      sorter: (a, b) => sortByNumberProperty(a, b, "backtesting_score"),
      render: (_, group) => (
        <div style={{ width: 70, maxWidth: 70 }}>
          <Logo src={group.image_path} />
          <ScoreTag
            score={group.backtesting_score}
            onClick={() => openScoreModal?.(group)}
            style={{
              maxWidth: 46,
              position: "relative",
              bottom: 18,
              right: -18,
            }}
          />
        </div>
      ),
    },
    {
      title: t("table.columns.group_name"),
      dataIndex: "name",
      width: 225,
      sortOrder:
        sortBy === GroupsSort.NAME
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      sorter: (a, b) => sortByStringProperty(a, b, "name"),
      render: (_, group) => (
        <Tooltip title={group.name} placement="topLeft">
          <div style={{ maxWidth: 225 }}>
            <Title level={4} $ellipsis={true}>
              {group.name}
            </Title>
            <GroupTags group={group} style={{ marginTop: 6 }} />
          </div>
        </Tooltip>
      ),
    },
    {
      title: t("table.columns.rating"),
      dataIndex: "avg_stars",
      sortOrder:
        sortBy === GroupsSort.RATING
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      sorter: (a, b) => sortByNumberProperty(a, b, "avg_stars"),
      render: (_, group) => (
        <GroupRate
          groupAvgStars={group.avg_stars || 0}
          groupNumReviewers={group.num_of_reviewers}
          starsSize={13}
        />
      ),
    },
    {
      title: t("table.columns.price"),
      dataIndex: "price_number",
      sortOrder:
        sortBy === GroupsSort.PRICE
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      sorter: (a, b) => sortByNumberProperty(a, b, "price_number"),
      render: (_, group) => (
        <>
          <GroupDollarRate
            priceNumber={group.price_number}
            style={{ marginBottom: 6 }}
          />
          <FreeOrFreeTrialTag
            hasFreeTrial={group.has_free_trial}
            isFree={group.is_free}
          />
        </>
      ),
    },
    {
      title: t("table.columns.pnl_6m"),
      dataIndex: "timeframe_to_backtesting_run_data.180",
      sortOrder:
        sortBy === GroupsSort.SIX_MONTH_PNL
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      sorter: getPnlSortCallback(180, displayCurrency),
      render: (_, group) => (
        <ChangeTag
          suffix={"%"}
          value={getNetPnlChange(group, 180, displayCurrency)}
        />
      ),
    },
    {
      title: t("table.columns.pnl_3m"),
      dataIndex: "timeframe_to_backtesting_run_data.90",
      sortOrder:
        sortBy === GroupsSort.THREE_MONTH_PNL
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      sorter: getPnlSortCallback(90, displayCurrency),
      render: (_, group) => (
        <ChangeTag
          suffix="%"
          value={getNetPnlChange(group, 90, displayCurrency)}
        />
      ),
    },
    {
      title: t("table.columns.one_month_pnl"),
      dataIndex: "timeframe_to_backtesting_run_data.30",
      width: 185,
      sortOrder:
        sortBy === GroupsSort.ONE_MONTH_PNL
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      sorter: getPnlSortCallback(30, displayCurrency),
      render: (_, group) => (
        <PnlChart group={group} height={45} showChange={true} />
      ),
    },
    {
      title: t("table.columns.risk"),
      dataIndex: "backtesting_score_breakdown",
      width: 125,
      sortOrder:
        sortBy === GroupsSort.RISK
          ? SORT_ORDER_MAPPING[sortDirection]
          : undefined,
      sorter: getRiskSortCallback(),
      render: (scoreBreakdown) => <RiskChart scoreBreakdown={scoreBreakdown} />,
    },
  ];

  const onGroupClick = (group: MarketplaceSubGroup, index: number) => {
    analytics?.marketplaceGroupClicked(
      buildMarketPlaceGroupEvent(group, index + 1)
    );
    history.push(
      getGroupExtraDetailsRoute(location.pathname, group.group_id.toString())
    );
  };

  const handleChange = getTableOnChangeFunction<GroupsSort>(
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    FIELD_NAME_TO_SORT,
    OPPOSITE_SORTS
  );

  return (
    <Table<MarketplaceSubGroup>
      columns={columns}
      dataSource={groups}
      rowClassName="u-cursorPointer"
      rowKey={(group) => group.group_id}
      locale={{ emptyText: emptyState || <TableLoadingEmpty /> }}
      loading={loading}
      pagination={{ showSizeChanger: false }}
      onRow={(group, index) => ({
        onClick: () => onGroupClick(group, index ?? 0),
      })}
      showSorterTooltip={false}
      onChange={handleChange}
    />
  );
};

const GroupsTable = (props: GroupsTableProps) => {
  const { isNewLook } = useTheme();
  return isNewLook ? (
    <NewLookGroupsTable {...props} />
  ) : (
    <ClassicGroupsTable {...props} />
  );
};

export default GroupsTable;

const Logo = styled.img`
  height: 40px;
  width: 40px;
  border-radius: 8px;
`;
